import { useState, useEffect, useCallback } from 'react'

interface WorkScopePriority {
  workScope: string
  workScopeId: number
  workScopeDisplay: string
  priorities: {
    ready: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill?: boolean }>
    notReady: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill?: boolean }>
    /** Goodwill entries (same shape, at bottom of column); isGoodwill is true */
    goodwill?: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill: true }>
  }
  completed: number
  total: number
  totalNeeded: number
  percentage: number
}

export function usePriorityData(regionId?: number | null) {
  const [data, setData] = useState<WorkScopePriority[]>([])
  const [isLoading, setIsLoading] = useState(false) // Start as false, only set to true when actually fetching
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!regionId) {
      console.log('[usePriorityData] No regionId provided, skipping fetch')
      setError('regionId is required')
      setIsLoading(false)
      return
    }

    console.log(`[usePriorityData] Fetching priority data for regionId: ${regionId}`)
    try {
      setIsLoading(true)
      setError(null)
      
      const response = await fetch(`/api/priority?regionId=${encodeURIComponent(regionId)}`)
      console.log(`[usePriorityData] Response status: ${response.status}, ok: ${response.ok}`)
      
      if (!response.ok) {
        const errorText = await response.text()
        setError(`API Error: ${response.status} - ${errorText}`)
        return
      }
      
      const result = await response.json()
      
      // Check if result is an array (direct data) or has success property
      if (Array.isArray(result)) {
        console.log(`[usePriorityData] Setting data (direct array, length: ${result.length})`)
        setData(result)
      } else if (result.success) {
        const dataToSet = result.data || result
        console.log(`[usePriorityData] Setting data (success object, length: ${Array.isArray(dataToSet) ? dataToSet.length : 'N/A'})`)
        setData(dataToSet)
      } else {
        console.error(`[usePriorityData] Error in response:`, result.error || result)
        setError(result.error || 'Failed to fetch priorities')
      }
    } catch (err) {
      console.error('[usePriorityData] Fetch error:', err)
      setError('Failed to fetch priorities')
    } finally {
      setIsLoading(false)
      console.log(`[usePriorityData] Finished loading`)
    }
  }, [regionId])

  const refreshData = async () => {
    if (!regionId) {
      setError('regionId is required')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      console.log(`Priority hook - Refreshing priorities via /api/priority/refresh for region ${regionId}`)
      
      // First, trigger the refresh endpoint to recalculate priorities
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
      
      const refreshResponse = await fetch('/api/priority/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ regionId }),
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      const refreshResult = await refreshResponse.json()
      console.log('Priority hook - Refresh response:', JSON.stringify(refreshResult, null, 2))
      
      if (!refreshResponse.ok) {
        throw new Error(refreshResult.error || 'Failed to refresh priorities')
      }
      
      // Then fetch the updated data
      console.log(`Priority hook - Fetching updated data from /api/priority for region ${regionId}`)
      const fetchResponse = await fetch(`/api/priority?regionId=${encodeURIComponent(regionId)}`)
      const fetchResult = await fetchResponse.json()
      
      console.log('Priority hook - Updated data response:', JSON.stringify(fetchResult, null, 2))
      
      // Check if result is an array (direct data) or has success property
      if (Array.isArray(fetchResult)) {
        console.log('Priority hook - Setting updated data (direct array):', JSON.stringify(fetchResult, null, 2))
        setData(fetchResult)
      } else if (fetchResult.success) {
        const dataToSet = fetchResult.data || fetchResult
        console.log('Priority hook - Setting updated data (success object):', JSON.stringify(dataToSet, null, 2))
        setData(dataToSet)
      } else {
        console.error('Priority hook - API error:', fetchResult.error)
        setError(fetchResult.error || 'Failed to fetch updated priorities')
      }
    } catch (err) {
      console.error('Priority hook - Refresh error:', err)
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request timed out. The priority calculation is taking too long.')
      } else {
        setError('Failed to refresh priorities')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    console.log(`[usePriorityData] useEffect triggered, regionId: ${regionId}`)
    if (regionId) {
      console.log(`[usePriorityData] Calling fetchData for regionId: ${regionId}`)
      fetchData()
    } else {
      console.log(`[usePriorityData] No regionId, skipping fetch`)
    }
  }, [regionId, fetchData])

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    refresh: refreshData
  }
}
